import React, { useState, useEffect, useCallback } from 'react';

const card = {
  background: '#fff',
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
  padding: '24px',
  marginBottom: '20px',
};

const label = {
  display: 'block',
  fontSize: '0.82rem',
  fontWeight: 600,
  color: '#475569',
  marginBottom: '6px',
  marginTop: '14px',
};

const input = {
  width: '100%',
  padding: '9px 12px',
  border: '1px solid #cbd5e1',
  borderRadius: '8px',
  fontSize: '0.9rem',
  boxSizing: 'border-box',
  outline: 'none',
};

const btnPrimary = {
  padding: '9px 20px',
  background: 'var(--color-primary, #0f5a30)',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: '0.88rem',
};

const btnSecondary = {
  padding: '9px 20px',
  background: '#f1f5f9',
  color: '#334155',
  border: '1px solid #cbd5e1',
  borderRadius: '8px',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: '0.88rem',
};

const badge = (ok) => ({
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: '20px',
  fontSize: '0.78rem',
  fontWeight: 700,
  background: ok ? '#dcfce7' : '#fef2f2',
  color:      ok ? '#15803d' : '#dc2626',
});

// ── Seção de E-mail ──────────────────────────────────────────────────────────
function SecaoEmail({ createApi, isMobile }) {
  const [cfg, setCfg]           = useState({});
  const [form, setForm]         = useState({});
  const [provider, setProvider] = useState('smtp');
  const [loading, setLoading]   = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg]           = useState('');
  const [err, setErr]           = useState('');
  const [testando, setTestando] = useState(false);
  const [conectando, setConectando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const api = createApi();
      const r = await api.get('/admin/config/email');
      setCfg(r.data);
      setProvider(r.data.provider || 'smtp');
      setForm({
        host:       r.data.host,
        port:       r.data.port,
        secure:     r.data.secure,
        user:       r.data.user,
        pass:       r.data.pass,
        from_name:  r.data.from_name,
        from_email: r.data.from_email,
      });
    } catch (e) {
      setErr(e.response?.data?.error || 'Erro ao carregar configuração.');
    } finally {
      setLoading(false);
    }
  }, [createApi]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === 'gmail_callback') {
        if (e.data.success) { setMsg('✅ Gmail conectado com sucesso!'); carregar(); }
        else                { setErr('Falha ao conectar Gmail.'); }
        setConectando(false);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [carregar]);

  const ativarProvider = async (p) => {
    setMsg(''); setErr('');
    // SMTP: só muda a view — usuário preenche as credenciais e clica Salvar
    if (p === 'smtp') {
      setProvider('smtp');
      return;
    }
    // Gmail API: salva imediatamente (credenciais vêm dos tokens OAuth)
    setSalvando(true);
    try {
      const api = createApi();
      const r = await api.put('/admin/config/email', { ...form, provider: p });
      setMsg(r.data.message);
      carregar(); // setProvider virá da resposta do servidor via carregar()
    } catch (e) {
      setErr(e.response?.data?.error || 'Erro ao salvar.');
    } finally {
      setSalvando(false);
    }
  };

  const salvar = async () => {
    setSalvando(true); setMsg(''); setErr('');
    try {
      const api = createApi();
      const r = await api.put('/admin/config/email', { ...form, provider });
      setMsg(r.data.message);
      carregar();
    } catch (e) {
      setErr(e.response?.data?.error || 'Erro ao salvar.');
    } finally {
      setSalvando(false);
    }
  };

  const testar = async () => {
    setTestando(true); setMsg(''); setErr('');
    try {
      const api = createApi();
      const r = await api.post('/admin/config/email/testar');
      setMsg(r.data.message);
    } catch (e) {
      setErr(e.response?.data?.error || 'Falha no teste.');
    } finally {
      setTestando(false);
    }
  };

  const conectarGmail = async () => {
    setConectando(true); setMsg(''); setErr('');
    try {
      const api = createApi();
      const r = await api.get('/admin/gmail/auth-url');
      const popup = window.open(r.data.url, 'gmail_oauth', 'width=520,height=620');
      if (!popup) { setErr('Pop-up bloqueado. Permita pop-ups para este site.'); setConectando(false); }
    } catch (e) {
      setErr(e.response?.data?.error || 'Erro ao obter URL de autorização.');
      setConectando(false);
    }
  };

  const desconectarGmail = async () => {
    setMsg(''); setErr('');
    try {
      const api = createApi();
      await api.delete('/admin/gmail/desconectar');
      setMsg('Gmail API desconectado.');
      carregar();
    } catch (e) {
      setErr(e.response?.data?.error || 'Erro ao desconectar.');
    }
  };

  if (loading) return <p style={{ color: '#64748b', padding: '12px 0' }}>Carregando…</p>;

  const gmailApi    = cfg.gmail_api || {};
  const smtpOk      = cfg.configurado;
  const gmailOk     = gmailApi.conectado;

  const cardProvider = (ativo, onClick, disabled) => ({
    flex: 1,
    minWidth: 140,
    padding: '14px 16px',
    borderRadius: 10,
    border: `2px solid ${ativo ? 'var(--color-primary,#0f5a30)' : '#e2e8f0'}`,
    background: ativo ? '#f0fdf4' : '#fff',
    cursor: disabled ? 'default' : 'pointer',
    textAlign: 'center',
    transition: 'all 0.15s',
    opacity: disabled ? 0.6 : 1,
  });

  return (
    <div style={card}>
      <h3 style={{ margin: '0 0 16px', fontSize: '1rem', color: '#1e293b' }}>📧 Servidor de E-mail</h3>

      {/* ── Seleção de provider ── */}
      <p style={{ margin: '0 0 10px', fontSize: '0.82rem', fontWeight: 600, color: '#475569' }}>
        Escolha o método de envio:
      </p>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>

        {/* Card SMTP */}
        <div style={cardProvider(provider === 'smtp', null, false)} onClick={() => !salvando && setProvider('smtp')}>
          <div style={{ fontSize: '1.5rem', marginBottom: 4 }}>📬</div>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: provider === 'smtp' ? '#15803d' : '#1e293b' }}>SMTP</div>
          <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 3 }}>
            {smtpOk ? `✅ ${form.user || 'configurado'}` : 'Requer host e senha'}
          </div>
          {provider === 'smtp' && (
            <div style={{ marginTop: 8 }}>
              <span style={{ fontSize: '0.72rem', background: '#0f5a30', color: '#fff', padding: '2px 10px', borderRadius: 20, fontWeight: 700 }}>
                ATIVO
              </span>
            </div>
          )}
          {provider !== 'smtp' && (
            <button
              onClick={(e) => { e.stopPropagation(); ativarProvider('smtp'); }}
              disabled={salvando}
              style={{ marginTop: 8, padding: '4px 12px', fontSize: '0.75rem', borderRadius: 6, border: '1px solid #cbd5e1', background: '#f1f5f9', cursor: 'pointer', fontWeight: 600 }}
            >
              {salvando ? '…' : 'Ativar SMTP'}
            </button>
          )}
        </div>

        {/* Card Gmail API */}
        <div style={cardProvider(provider === 'gmail_api', null, false)} onClick={() => !salvando && setProvider('gmail_api')}>
          <div style={{ fontSize: '1.5rem', marginBottom: 4 }}>🔑</div>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: provider === 'gmail_api' ? '#15803d' : '#1e293b' }}>Gmail API</div>
          <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 3 }}>
            {gmailOk ? `✅ ${gmailApi.email}` : 'OAuth2 · sem senha'}
          </div>
          {provider === 'gmail_api' && (
            <div style={{ marginTop: 8 }}>
              <span style={{ fontSize: '0.72rem', background: '#0f5a30', color: '#fff', padding: '2px 10px', borderRadius: 20, fontWeight: 700 }}>
                ATIVO
              </span>
            </div>
          )}
          {provider !== 'gmail_api' && (
            <button
              onClick={(e) => { e.stopPropagation(); ativarProvider('gmail_api'); }}
              disabled={salvando}
              style={{ marginTop: 8, padding: '4px 12px', fontSize: '0.75rem', borderRadius: 6, border: '1px solid #cbd5e1', background: '#f1f5f9', cursor: 'pointer', fontWeight: 600 }}
            >
              {salvando ? '…' : 'Ativar Gmail API'}
            </button>
          )}
        </div>
      </div>

      {/* ── Painel Gmail API ── */}
      {provider === 'gmail_api' && (
        <div>
          <div style={{
            background: gmailOk ? '#f0fdf4' : '#fefce8',
            border: `1px solid ${gmailOk ? '#86efac' : '#fde047'}`,
            borderRadius: 10, padding: '14px 18px', marginBottom: 14,
          }}>
            {gmailOk ? (
              <>
                <p style={{ margin: 0, fontSize: '0.88rem', color: '#15803d', fontWeight: 600 }}>
                  Conectado como {gmailApi.email}
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#166534' }}>
                  Envio via OAuth2 (HTTPS) — funciona mesmo com SMTP bloqueado pelo servidor.
                </p>
              </>
            ) : (
              <>
                <p style={{ margin: 0, fontSize: '0.88rem', color: '#92400e', fontWeight: 600 }}>
                  Gmail API não conectado
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#78350f' }}>
                  Clique em "Conectar Gmail" abaixo para autorizar via Google.
                </p>
              </>
            )}
          </div>

          <label style={label}>Nome do remetente</label>
          <input style={input} value={form.from_name || ''} onChange={e => setForm(f => ({ ...f, from_name: e.target.value }))} placeholder="Allma Pilates" />
          <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: '4px 0 12px' }}>
            O e-mail remetente será o da conta Google autorizada.
          </p>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              style={{ ...btnSecondary, background: gmailOk ? '#fef2f2' : '#f1f5f9', color: gmailOk ? '#dc2626' : '#334155', borderColor: gmailOk ? '#fca5a5' : '#cbd5e1' }}
              onClick={gmailOk ? desconectarGmail : conectarGmail}
              disabled={conectando}
            >
              {conectando ? 'Aguardando…' : gmailOk ? '🔌 Desconectar Gmail' : '🔑 Conectar Gmail'}
            </button>
          </div>

          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', marginTop: 14 }}>
            <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b' }}>
              <strong>Pré-requisito:</strong> Adicione a URI de callback no Google Cloud Console:<br />
              <code style={{ background: '#e2e8f0', padding: '2px 6px', borderRadius: 4, fontSize: '0.75rem' }}>
                {window.location.origin}/api/admin/gmail/callback
              </code>
            </p>
          </div>
        </div>
      )}

      {/* ── Painel SMTP ── */}
      {provider === 'smtp' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 16px' }}>
            <div>
              <label style={label}>Host SMTP</label>
              <input style={input} value={form.host || ''} onChange={e => setForm(f => ({ ...f, host: e.target.value }))} placeholder="smtp.gmail.com" />
            </div>
            <div>
              <label style={label}>Porta</label>
              <input style={input} type="number" value={form.port || 587} onChange={e => setForm(f => ({ ...f, port: Number(e.target.value) }))} />
            </div>
            <div>
              <label style={label}>Usuário (e-mail remetente)</label>
              <input style={input} value={form.user || ''} onChange={e => setForm(f => ({ ...f, user: e.target.value }))} placeholder="noreply@seudominio.com.br" />
            </div>
            <div>
              <label style={label}>Senha / App Password</label>
              <input style={input} type="password" value={form.pass || ''} onChange={e => setForm(f => ({ ...f, pass: e.target.value }))} placeholder="••••••••" />
            </div>
            <div>
              <label style={label}>Nome do remetente</label>
              <input style={input} value={form.from_name || ''} onChange={e => setForm(f => ({ ...f, from_name: e.target.value }))} placeholder="Allma Pilates" />
            </div>
            <div>
              <label style={label}>E-mail de exibição (from)</label>
              <input style={input} value={form.from_email || ''} onChange={e => setForm(f => ({ ...f, from_email: e.target.value }))} placeholder="Igual ao usuário se vazio" />
            </div>
          </div>
          <div style={{ marginTop: '6px' }}>
            <label style={{ ...label, marginTop: 8 }}>
              <input type="checkbox" checked={!!form.secure} onChange={e => setForm(f => ({ ...f, secure: e.target.checked }))} style={{ marginRight: 6 }} />
              Conexão SSL/TLS (porta 465)
            </label>
          </div>
        </>
      )}

      {msg && <p style={{ color: '#15803d', fontWeight: 600, margin: '12px 0 0' }}>{msg}</p>}
      {err && <p style={{ color: '#dc2626', margin: '12px 0 0' }}>{err}</p>}

      <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
        <button style={btnPrimary} onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando…' : 'Salvar'}
        </button>
        <button
          style={btnSecondary}
          onClick={testar}
          disabled={testando || (provider === 'gmail_api' && !gmailOk)}
        >
          {testando ? 'Testando…' : 'Enviar e-mail de teste'}
        </button>
      </div>
    </div>
  );
}

// ── Seção de Google Calendar ─────────────────────────────────────────────────
function SecaoGCal({ createApi, isMobile }) {
  const [cfg, setCfg]         = useState({});
  const [targetCalendar, setTargetCalendar] = useState('');
  const [saJson, setSaJson]   = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]         = useState('');
  const [err, setErr]         = useState('');
  const [testando, setTestando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const api = createApi();
      const r = await api.get('/admin/config/gcal');
      setCfg(r.data);
      setTargetCalendar(r.data.target_calendar || '');
    } catch (e) {
      setErr(e.response?.data?.error || 'Erro ao carregar configuração.');
    } finally {
      setLoading(false);
    }
  }, [createApi]);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    setMsg(''); setErr('');
    try {
      const api = createApi();
      const r = await api.put('/admin/config/gcal', {
        target_calendar: targetCalendar,
        service_account_json: saJson.trim() || undefined,
      });
      setMsg(r.data.message);
      setSaJson('');
      carregar();
    } catch (e) {
      setErr(e.response?.data?.error || 'Erro ao salvar.');
    }
  };

  const testar = async () => {
    setTestando(true); setMsg(''); setErr('');
    try {
      const api = createApi();
      const r = await api.post('/admin/config/gcal/testar');
      setMsg(r.data.message);
    } catch (e) {
      setErr(e.response?.data?.error || 'Falha no teste.');
    } finally {
      setTestando(false);
    }
  };

  const lerArquivo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setSaJson(ev.target.result);
    reader.readAsText(file);
    e.target.value = '';
  };

  if (loading) return <p style={{ color: '#64748b', padding: '12px 0' }}>Carregando…</p>;

  const sa = cfg.service_account;

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: '1rem', color: '#1e293b' }}>📅 Google Calendar (Service Account)</h3>
        <span style={badge(sa?.configurado)}>{sa?.configurado ? 'Configurado' : 'Não configurado'}</span>
      </div>

      {sa?.configurado && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '12px 16px', margin: '12px 0' }}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#15803d' }}>
            <strong>Conta de serviço:</strong> {sa.client_email}
            {sa.project_id && <> &nbsp;·&nbsp; Projeto: {sa.project_id}</>}
            {sa.origem === 'arquivo' && <> &nbsp;·&nbsp; <em>(via arquivo em disco)</em></>}
          </p>
        </div>
      )}

      <label style={label}>E-mail do calendário alvo</label>
      <input
        style={input}
        value={targetCalendar}
        onChange={e => setTargetCalendar(e.target.value)}
        placeholder="email@gmail.com"
      />
      <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: '4px 0 0' }}>
        E-mail da conta Google proprietária do calendário. A service account precisa ter permissão neste calendário.
      </p>

      <label style={{ ...label, marginTop: 18 }}>Credenciais da Service Account (JSON)</label>
      <div style={{ display: 'flex', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
        <label style={{ ...btnSecondary, cursor: 'pointer', display: 'inline-block' }}>
          📂 Escolher arquivo .json
          <input type="file" accept=".json" onChange={lerArquivo} style={{ display: 'none' }} />
        </label>
        {saJson && (
          <span style={{ color: '#15803d', fontSize: '0.82rem', alignSelf: 'center' }}>✅ Arquivo carregado</span>
        )}
      </div>
      <textarea
        style={{ ...input, minHeight: 120, fontFamily: 'monospace', fontSize: '0.78rem', resize: 'vertical' }}
        value={saJson}
        onChange={e => setSaJson(e.target.value)}
        placeholder={'Cole aqui o conteúdo do arquivo JSON da service account...\n{\n  "type": "service_account",\n  "project_id": "...",\n  "client_email": "...",\n  "private_key": "...",\n  ...\n}'}
      />
      <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: '4px 0 0' }}>
        Deixe em branco para manter as credenciais atuais.
      </p>

      {msg && <p style={{ color: '#15803d', fontWeight: 600, margin: '12px 0 0' }}>{msg}</p>}
      {err && <p style={{ color: '#dc2626', margin: '12px 0 0' }}>{err}</p>}

      <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
        <button style={btnPrimary} onClick={salvar}>Salvar</button>
        <button style={btnSecondary} onClick={testar} disabled={testando || !sa?.configurado}>
          {testando ? 'Testando…' : 'Testar conexão'}
        </button>
      </div>

      {!sa?.configurado && (
        <div style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: 8, padding: '12px 16px', marginTop: 16 }}>
          <p style={{ margin: 0, fontSize: '0.83rem', color: '#92400e' }}>
            <strong>Como configurar:</strong><br />
            1. No Google Cloud Console, crie uma Service Account<br />
            2. Gere uma chave JSON e faça o download<br />
            3. No Google Calendar, compartilhe o calendário com o e-mail da service account (com permissão de edição)<br />
            4. Cole o JSON acima e defina o e-mail do calendário alvo
          </p>
        </div>
      )}
    </div>
  );
}

// ── Seção Google OAuth Credentials ──────────────────────────────────────────
function SecaoGoogleOAuth({ createApi, isMobile }) {
  const [cfg, setCfg]     = useState({});
  const [form, setForm]   = useState({});
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]     = useState('');
  const [err, setErr]     = useState('');

  const carregar = useCallback(async () => {
    try {
      const api = createApi();
      const r = await api.get('/admin/config/google-oauth');
      setCfg(r.data);
      setForm({
        client_id:      r.data.client_id      || '',
        client_secret:  r.data.client_secret  || '',
        gcal_redirect:  r.data.gcal_redirect  || '',
        gmail_redirect: r.data.gmail_redirect || '',
      });
    } catch (e) {
      setErr(e.response?.data?.error || 'Erro ao carregar.');
    } finally {
      setLoading(false);
    }
  }, [createApi]);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    setMsg(''); setErr('');
    try {
      const api = createApi();
      const r = await api.put('/admin/config/google-oauth', form);
      setMsg(r.data.message);
      carregar();
    } catch (e) {
      setErr(e.response?.data?.error || 'Erro ao salvar.');
    }
  };

  if (loading) return <p style={{ color: '#64748b', padding: '12px 0' }}>Carregando…</p>;

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: '1rem', color: '#1e293b' }}>🔑 Google OAuth (Client ID / Secret)</h3>
        <span style={badge(cfg.configurado)}>
          {cfg.configurado
            ? `Configurado · ${cfg.origem === 'banco' ? 'via painel' : 'via .env'}`
            : 'Não configurado'}
        </span>
      </div>

      <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '10px 0 2px' }}>
        Credenciais OAuth do Google Cloud Console. Usadas pelo Google Calendar e Gmail API.
        Se preenchido aqui, substitui os valores do arquivo <code>.env</code>.
      </p>

      <label style={label}>Client ID</label>
      <input
        style={input}
        value={form.client_id || ''}
        onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}
        placeholder="445296907972-xxxx.apps.googleusercontent.com"
      />

      <label style={label}>Client Secret</label>
      <input
        style={input}
        type="password"
        value={form.client_secret || ''}
        onChange={e => setForm(f => ({ ...f, client_secret: e.target.value }))}
        placeholder="••••••••"
      />

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 16px' }}>
        <div>
          <label style={label}>Redirect URI — Google Calendar</label>
          <input
            style={input}
            value={form.gcal_redirect || ''}
            onChange={e => setForm(f => ({ ...f, gcal_redirect: e.target.value }))}
            placeholder="https://seudominio.com.br/api/admin/gcal/callback"
          />
        </div>
        <div>
          <label style={label}>Redirect URI — Gmail API</label>
          <input
            style={input}
            value={form.gmail_redirect || ''}
            onChange={e => setForm(f => ({ ...f, gmail_redirect: e.target.value }))}
            placeholder="https://seudominio.com.br/api/admin/gmail/callback"
          />
        </div>
      </div>

      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', marginTop: 14 }}>
        <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b' }}>
          Ambas as URIs de redirect precisam estar cadastradas no Google Cloud Console em<br />
          <strong>APIs & Services → Credenciais → OAuth 2.0 Client IDs → URIs de redirecionamento autorizadas</strong>.
        </p>
      </div>

      {msg && <p style={{ color: '#15803d', fontWeight: 600, margin: '12px 0 0' }}>{msg}</p>}
      {err && <p style={{ color: '#dc2626', margin: '12px 0 0' }}>{err}</p>}

      <div style={{ marginTop: 18 }}>
        <button style={btnPrimary} onClick={salvar}>Salvar</button>
      </div>
    </div>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────
export function GerenciadorIntegracoes({ createApi, isMobile }) {
  return (
    <div style={{ padding: isMobile ? '16px 12px' : '24px' }}>
      <h2 style={{ margin: '0 0 20px', fontSize: '1.1rem', color: '#1e293b' }}>
        🔌 Integrações
      </h2>
      <SecaoGoogleOAuth createApi={createApi} isMobile={isMobile} />
      <SecaoEmail       createApi={createApi} isMobile={isMobile} />
      <SecaoGCal        createApi={createApi} isMobile={isMobile} />
    </div>
  );
}
